import apiClient from './apiClient'

export const login = async (usernameOrEmail, password) => {
  try {
    const response = await apiClient.post('/auth/login/', { username_or_email: usernameOrEmail, password })
    const { user, access, refresh, is_staff } = response.data
    
    // Guardar datos en localStorage
    localStorage.setItem('accessToken', access)
    localStorage.setItem('refreshToken', refresh)
    localStorage.setItem('isAdmin', is_staff)
    localStorage.setItem('userId', user.id)
    localStorage.setItem('username', user.username || user.email)
    
    // Invalidar caché de productos para forzar recarga con precios
    localStorage.removeItem('products')
    localStorage.removeItem('productsTimestamp')
    
    return {
      user,
      is_admin: is_staff
    }
  } catch (error) {
    console.error('Error al iniciar sesión:', error)
    
    // Preservar la respuesta completa del error para casos especiales
    if (error.response?.data) {
      const errorData = error.response.data;
      
      // Si es un error especial (cuenta no verificada, etc.), preservar la estructura
      if (errorData.error && typeof errorData.error === 'string') {
        const customError = new Error(errorData.message || 'Error al iniciar sesión');
        customError.response = error.response;
        throw customError;
      }
    }
    
    throw new Error(
      error.response?.data?.error || 
      'Error al iniciar sesión. Por favor, verifica tus credenciales.'
    )
  }
}

export const register = async (userData) => {
  try {
    const response = await apiClient.post('/auth/register/', userData);
    
    if (response.data) {
      return {
        message: response.data.message,
        email: response.data.email,
        userId: response.data.user_id
      };
    }
    
    throw new Error(response.data?.message || 'Error en el registro');
  } catch (error) {
    // Preservar la respuesta completa del error para casos especiales
    if (error.response?.data) {
      const errorData = error.response.data;
      
      // Si es un error especial (cuenta existente, etc.), preservar la estructura
      if (errorData.error && typeof errorData.error === 'string') {
        const customError = new Error(errorData.message || 'Error en el registro');
        customError.response = error.response;
        throw customError;
      }
      
      // Si es un array de errores de validación
      if (errorData.error && Array.isArray(errorData.error)) {
        const customError = new Error('Errores de validación');
        customError.errors = errorData.error;
        throw customError;
      }
    }
    throw error;
  }
};

export const logout = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('isAdmin');
  localStorage.removeItem('userId');
  localStorage.removeItem('username');
  localStorage.removeItem('intendedRoute');
  localStorage.removeItem('userPermissions');
  localStorage.removeItem('userPermissionsTimestamp');
  
  // Invalidar caché de productos para forzar recarga sin precios
  localStorage.removeItem('products');
  localStorage.removeItem('productsTimestamp');
};

export const isAuthenticated = () => {
  return localStorage.getItem('accessToken') !== null;
};

export const isAdmin = () => {
  return localStorage.getItem('isAdmin') === 'true';
};

export const getUsername = () => {
  return localStorage.getItem('username')
}

export const getAccessToken = () => {
  return localStorage.getItem('accessToken')
}

export const getUserInfo = async () => {
  try {
    const token = getAccessToken()
    const response = await apiClient.get('/auth/profile/', {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
    return response.data
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error al obtener información del usuario')
  }
}

export const verifyPermissions = async () => {
  try {
    const token = getAccessToken()
    if (!token) {
      throw new Error('No hay token de acceso')
    }

    // Verificar si tenemos permisos en caché (válidos por 5 minutos)
    const cachedPermissions = localStorage.getItem('userPermissions')
    const permissionsTimestamp = localStorage.getItem('userPermissionsTimestamp')
    const now = Date.now()
    
    if (cachedPermissions && permissionsTimestamp) {
      const cacheAge = now - parseInt(permissionsTimestamp)
      if (cacheAge < 5 * 60 * 1000) { // 5 minutos
        return JSON.parse(cachedPermissions)
      }
    }

    // Si no hay caché válido, consultar al backend
    const response = await apiClient.get('/auth/verify-permissions/', {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
    
    const permissions = response.data
    
    // Guardar en caché
    localStorage.setItem('userPermissions', JSON.stringify(permissions))
    localStorage.setItem('userPermissionsTimestamp', now.toString())
    
    // Actualizar isAdmin en localStorage con el valor del backend
    localStorage.setItem('isAdmin', permissions.is_staff.toString())
    
    return permissions
  } catch (error) {
    console.error('Error verificando permisos:', error)
    // Si hay error, limpiar caché y redirigir a login
    localStorage.removeItem('userPermissions')
    localStorage.removeItem('userPermissionsTimestamp')
    localStorage.removeItem('isAdmin')
    throw error
  }
}

export const hasAdminPermission = async () => {
  try {
    const permissions = await verifyPermissions()
    return permissions.permissions.can_access_admin
  } catch (error) {
    return false
  }
}

export const checkPermission = async (permission) => {
  try {
    const permissions = await verifyPermissions()
    return permissions.permissions[permission] || false
  } catch (error) {
    return false
  }
}

export const updateUserProfile = async (userData) => {
  try {
    const response = await apiClient.put('/auth/profile/', userData)
    return response.data
  } catch (error) {
    throw error.response?.data || error
  }
}

export const changePassword = async (currentPassword, newPassword) => {
  try {
    const token = getAccessToken()
    const response = await apiClient.post('/auth/change-password/', {
      old_password: currentPassword,
      new_password: newPassword
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
    return response.data
  } catch (error) {
    const message = error.response?.data?.message || 'Error al cambiar la contraseña'
    throw new Error(message)
  }
}

export const verifyEmail = async (data) => {
  const response = await apiClient.post('/auth/verify-email/', {
    email: data.email,
    code: data.code
  });
  return response.data;
};

export const resendVerification = async (email) => {
  const response = await apiClient.post('/auth/resend-verification/', {
    email
  });
  return response.data;
};

export const requestVerificationCode = async (type, newEmail = null, password = null) => {
  try {
    const response = await apiClient.post('/auth/verify-change/', {
      type,
      new_email: newEmail,
      password: password
    })
    return response.data
  } catch (error) {
    throw error.response?.data || error
  }
}

export const updateEmailWithVerification = async (email, verificationCode) => {
  try {
    const response = await apiClient.put('/auth/profile/', {
      email,
      verification_code: verificationCode
    })
    return response.data
  } catch (error) {
    throw error.response?.data || error
  }
}

export const getUsers = async (filters = {}) => {
  try {
    const token = localStorage.getItem('accessToken');
    
    // Construir parámetros de consulta
    const params = new URLSearchParams();
    if (filters.username) params.append('username', filters.username);
    if (filters.email) params.append('email', filters.email);
    if (filters.status) params.append('status', filters.status);
    if (filters.user_type) params.append('user_type', filters.user_type);
    if (filters.page) params.append('page', filters.page);
    if (filters.page_size) params.append('page_size', filters.page_size);
    
    const queryString = params.toString();
    const url = `/auth/users/${queryString ? `?${queryString}` : ''}`;
    
    console.log('Sending request to:', url); // Para debug
    console.log('Filters being sent:', filters); // Para debug
    
    const response = await apiClient.get(url, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error obteniendo usuarios:', error);
    throw error;
  }
}

export const getUserDetails = async (userId) => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await apiClient.get(`/auth/users/${userId}/`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error obteniendo detalles del usuario:', error);
    throw error;
  }
}

export const deleteUser = async (userId) => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await apiClient.delete(`/auth/users/${userId}/`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error eliminando usuario:', error);
    throw error;
  }
}

export const toggleUserStatus = async (userId, isActive) => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await apiClient.patch(`/auth/users/${userId}/`, {
      is_active: isActive
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error cambiando estado del usuario:', error);
    throw error;
  }
}

export const createUserAsAdmin = async (userData) => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await apiClient.post('/auth/admin/create-user/', userData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    return response.data;
  } catch (error) {
    // Preservar la respuesta completa del error para casos especiales
    if (error.response?.data) {
      const errorData = error.response.data;
      
      // Si es un error especial (cuenta existente, etc.), preservar la estructura
      if (errorData.error && typeof errorData.error === 'string') {
        const customError = new Error(errorData.message || 'Error al crear el usuario');
        customError.response = error.response;
        throw customError;
      }
      
      // Si es un array de errores de validación
      if (errorData.error && Array.isArray(errorData.error)) {
        const customError = new Error('Errores de validación');
        customError.errors = errorData.error;
        throw customError;
      }
    }
    throw error;
  }
}